import { Link } from 'react-router-dom';
import LegalPage from './LegalPage';

// DRAFT. Have counsel review before publishing, and resolve every <Fill>.
// Sections 3 and 5 describe what this site actually does today (contact form ->
// reCAPTCHA -> Lambda -> SendGrid; hosted on AWS Amplify; Google Fonts; no
// analytics or ad cookies). If you add analytics, a chat widget, or a new
// processor, update this page in the same change.
function PrivacyPolicy() {
  return (
    <LegalPage
      title="Privacy Policy"
      lead="What information InfoRight collects, why, and what choices you have."
      effectiveDate="September 30, 2026"
    >
      <section>
        <h2>1. Who we are and what this covers</h2>
        <p>
          InfoRight, Inc. ("InfoRight", "we", "us") builds safety, inspection, certification, and
          data integration applications for mining operations. This Privacy Policy explains how we
          handle personal information:
        </p>
        <ul>
          <li>
            when you visit our website at www.inforight.com (the "Site") or contact us;
          </li>
          <li>about the business contacts at our customers, prospects, and partners; and</li>
          <li>
            inside our subscription applications (the "Service") on behalf of our customers, as
            described in Section 4.
          </li>
        </ul>
      </section>

      <section>
        <h2>2. Our role for customer data</h2>
        <p>
          Our customers are mining companies and other organizations. They decide what information
          their employees, contractors, and site visitors enter into the Service and how it is used.
          For that information ("Customer Data"), we act as a service provider or processor for the
          customer, and we handle it under our{' '}
          <Link to="/legal/subscription-agreement">SaaS Subscription Agreement</Link>,{' '}
          <Link to="/legal/data-processing-addendum">Data Processing Addendum</Link>, and the
          customer's instructions. If you use the Service through your employer or as a visitor to
          a mine site, that organization's privacy notice applies to your information, and you
          should direct privacy questions and requests to them first. If you contact us instead, we
          will refer your request to them.
        </p>
      </section>

      <section>
        <h2>3. Information we collect through the Site</h2>
        <h3>Information you give us</h3>
        <p>
          When you use our contact form, we collect your name, email address, and message. You can
          also choose to give us your company, phone number, and area of interest. We also keep the
          information you share with us by email, by phone, or in meetings.
        </p>
        <h3>Information collected automatically</h3>
        <ul>
          <li>
            <strong>Spam protection.</strong> Our contact form uses Google reCAPTCHA to tell people
            from bots. reCAPTCHA collects hardware and software information, such as device and
            browser data, and sends it to Google. Google's{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>{' '}
            apply to its use of that information.
          </li>
          <li>
            <strong>Server logs.</strong> Our hosting provider automatically records standard
            technical information when you load the Site. This includes your IP address, browser
            type, the pages you request, and the date and time.
          </li>
          <li>
            <strong>Web fonts.</strong> The Site loads fonts from Google Fonts, so your browser sends
            your IP address to Google when a page loads.
          </li>
        </ul>
        <p>
          The Site does not currently use analytics, advertising cookies, or cross-site tracking. If
          that changes, we will update this Policy first.
        </p>
      </section>

      <section>
        <h2>4. Information processed in the Service</h2>
        <p>
          Depending on which applications a customer uses, Customer Data in the Service can include:
        </p>
        <ul>
          <li>
            <strong>User profile information</strong>, such as name, work email, phone number,
            employer, job role, and assigned sites.
          </li>
          <li>
            <strong>Training and certification records</strong>, including MSHA Form 5000-23
            documentation, training dates, and expiration dates.
          </li>
          <li>
            <strong>Inspection records</strong>, such as vehicle, equipment, and area inspection
            results, defects reported, and who performed each inspection and when.
          </li>
          <li>
            <strong>Site visitor information</strong>, such as name, company, training
            acknowledgments, approvals, and visit dates.
          </li>
          <li>
            <strong>Data from integrated systems</strong> that the customer chooses to connect, such
            as equipment lists and project or financial data.
          </li>
          <li>
            <strong>Usage and device information</strong>, such as log-in times, device type, app
            version, and IP address, which we use to operate and secure the Service.
          </li>
          <li>
            <strong>Photos, signatures, and device location</strong> attached to inspections and sign-offs.
          </li>
        </ul>
        <p>
          We use Customer Data only to provide, support, secure, and improve the Service for that
          customer, or as the customer otherwise instructs and the law requires. We do not sell
          Customer Data or use it for advertising.
        </p>
      </section>

      <section>
        <h2>5. How we use information</h2>
        <p>We use the personal information described in Sections 3 and 4 to:</p>
        <ul>
          <li>respond to inquiries, give demos, and prepare quotes;</li>
          <li>set up, provide, maintain, and support the Service;</li>
          <li>manage customer accounts, billing, and our business relationships;</li>
          <li>send service notices, security alerts, and administrative messages;</li>
          <li>protect the Site and Service against spam, fraud, abuse, and security threats;</li>
          <li>comply with law and enforce our agreements; and</li>
          <li>
            with your consent or where the law allows, send occasional product updates to business
            contacts. You can opt out at any time.
          </li>
        </ul>
      </section>

      <section>
        <h2>6. How we share information</h2>
        <p>
          We do not sell personal information, and we do not share it for cross-context behavioral
          advertising. We share it only as follows:
        </p>
        <ul>
          <li>
            <strong>Service providers</strong> who help us run our business and are bound to protect
            the information. These include Amazon Web Services and Microsoft Azure (cloud hosting,
            storage, and infrastructure), Twilio SendGrid (delivery of contact form messages and
            email notifications), Twilio (delivery of SMS text notifications), Google (reCAPTCHA
            and web fonts), and Stripe (payment processing). A current list of the
            providers that process Customer Data is in Annex 3 of our{' '}
            <Link to="/legal/data-processing-addendum">Data Processing Addendum</Link>.
          </li>
          <li>
            <strong>Within a customer's organization.</strong> Customer administrators and
            authorized personnel can see Customer Data in their account.
          </li>
          <li>
            <strong>Integrations the customer enables</strong>, which send data to or from the
            customer's other systems.
          </li>
          <li>
            <strong>Legal and safety reasons</strong>, when needed to comply with law, legal
            process, or a lawful government request, or to protect the rights, property, or safety
            of InfoRight, our customers, or others.
          </li>
          <li>
            <strong>Business transfers</strong>, as part of a merger, acquisition, financing, or
            sale of assets, subject to confidentiality protections and this Policy.
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Data retention</h2>
        <p>
          We keep contact form submissions and business correspondence for as long as needed to
          respond and to manage the business relationship. We keep Customer Data for the length of the
          customer's subscription and delete or return it afterward as the Subscription Agreement
          provides. Some records may need to be kept longer to meet legal obligations, resolve
          disputes, or enforce our agreements. Customers are responsible for keeping regulatory
          records, such as MSHA training records, for the periods the law requires.
        </p>
      </section>

      <section>
        <h2>8. Security</h2>
        <p>
          We use administrative, technical, and physical safeguards to protect personal
          information. These include encryption in transit, access controls, and audit logging. No
          system is completely secure. If we learn of a security incident affecting personal
          information, we will notify affected customers and individuals as the law and our
          agreements require.
        </p>
      </section>

      <section>
        <h2>9. Your choices and rights</h2>
        <p>
          Depending on where you live, you may have the right to ask for access to, correction of,
          or deletion of your personal information, to receive a copy of it, or to opt out of
          certain processing. To make a request about information we hold for our own purposes,
          such as Site or contact form data, email{' '}
          <a href="mailto:support@inforight.com">support@inforight.com</a>. We will verify your
          request, respond within the time the law requires, and will not treat you differently for
          exercising your rights. You may use an authorized agent where the law allows.
        </p>
        <p>
          For information in the Service, please contact the organization that gave you access. We
          will help our customers respond to your request.
        </p>
        <p>
          You can unsubscribe from marketing email at any time using the link in the email or by
          contacting us. We will still send essential service and account messages.
        </p>
      </section>

      <section>
        <h2>10. Children</h2>
        <p>
          The Site and Service are intended for businesses and are not directed to children under
          16. We do not knowingly collect personal information from children. If you believe a
          child has given us personal information, contact us and we will delete it.
        </p>
      </section>

      <section>
        <h2>11. Where information is stored</h2>
        <p>
          InfoRight is based in the United States, and we store and process information in the
          United States. If you access the Site or Service from outside the United States, your 
          information will be transferred to, stored, and processed in the United States.
        </p>
      </section>

      <section>
        <h2>12. Changes to this Policy</h2>
        <p>
          We may update this Policy from time to time. When we do, we will post the new version on
          this page and change the effective date. For material changes, we will give additional
          notice, such as by email to customer administrators.
        </p>
      </section>

      <section>
        <h2>13. Contact us</h2>
        <p>
          Questions about this Policy or our privacy practices can be sent to{' '}
          <a href="mailto:support@inforight.com">support@inforight.com</a> or to InfoRight,
          Inc., 8977 South 1300 West #2001, West Jordan, UT 84088.
        </p>
      </section>
    </LegalPage>
  );
}

export default PrivacyPolicy;
